import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { colors, fonts } from '../brand';
import Disclaimer from '../components/Disclaimer';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming'
];

const questions = [
  {
    id: 'persona',
    question: 'Which of these best describes your situation right now?',
    subtext: 'Choose the one that feels closest — there\'s no wrong answer.',
    type: 'select',
    options: [
      { value: 'sahm', label: 'Stay-at-home mom — out of the workforce and am financially dependent' },
      { value: 'working_mom', label: 'Working mom — juggling career, kids, and more' },
      { value: 'professional', label: 'Working woman — super career-focused (probably breadwinner), no kids' },
      { value: 'midlife', label: 'Midlife — retirement not too far away, long marriage, significant shared assets, adult children' },
    ]
  },
  {
    id: 'has_kids',
    question: 'Do you have minor children who are part of this divorce?',
    subtext: null,
    type: 'boolean',
    options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ]
  },
  {
    id: 'financial_abuse',
    question: 'Are you concerned about financial control, hidden assets, or being cut off from money?',
    subtext: 'This is more common than people think. Your answer helps us prioritize the right steps.',
    type: 'boolean',
    options: [
      { value: true, label: 'Yes — this is a concern for me' },
      { value: false, label: 'No — finances have been relatively transparent' },
    ]
  },
  {
    id: 'complexity_level',
    question: 'How would you describe your overall divorce situation?',
    subtext: 'Be honest — the more accurately we understand your situation, the better your roadmap.',
    type: 'select',
    options: [
      { value: 'low', label: 'Relatively straightforward — we agree on most things and want to keep it civil' },
      { value: 'moderate', label: 'Some disagreements — we\'ll need to negotiate but it\'s workable' },
      { value: 'high', label: 'High-conflict — significant assets, hidden finances, coercive control, or manipulation involved' },
    ]
  }
];

const bicQuestions = [
  {
    id: 'has_lawyer',
    question: 'Are you working with a lawyer?',
    subtext: 'This helps us point you to the most relevant stories.',
    type: 'boolean',
    options: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ]
  },
  {
    id: 'court_issue',
    question: 'What is the issue?',
    subtext: null,
    type: 'select',
    options: [
      { value: 'finances', label: 'Finances' },
      { value: 'custody', label: 'Custody' },
      { value: 'both', label: 'Both' },
    ]
  },
  {
    id: 'court_level',
    question: 'Which court are you in?',
    subtext: null,
    type: 'select',
    options: [
      { value: 'district', label: 'District Court' },
      { value: 'appeals', label: 'Court of Appeals' },
      { value: 'supreme', label: 'State Supreme Court' },
    ]
  },
];

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(null); // null | 'standard' | 'back_in_court'
  const [bicStep, setBicStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [bicAnswers, setBicAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const { token, refreshUser } = useAuth();
  const navigate = useNavigate();

  const isBackInCourt = mode === 'back_in_court';
  const current = isBackInCourt ? bicQuestions[bicStep] : questions[step];
  const totalSteps = 4; // 1 shared + 3 follow-up in either path
  const currentStepNum = isBackInCourt ? 1 + bicStep + 1 : step + 1;
  const pct = Math.round((currentStepNum / totalSteps) * 100);

  const handleAnswer = (value) => {
    if (step === 0 && mode === null) {
      // Q1 answered — branch
      const newAnswers = { ...answers, persona: value };
      setAnswers(newAnswers);
      if (value === 'back_in_court') {
        setMode('back_in_court');
      } else {
        setMode('standard');
        setStep(1);
      }
      return;
    }

    if (isBackInCourt) {
      const newBic = { ...bicAnswers, [current.id]: value };
      setBicAnswers(newBic);
      if (bicStep < bicQuestions.length - 1) {
        setBicStep(bicStep + 1);
      } else {
        submitBackInCourt(newBic);
      }
      return;
    }

    const newAnswers = { ...answers, [current.id]: value };
    setAnswers(newAnswers);
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const submitQuiz = async (finalAnswers) => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/quiz/submit`, {
        persona: finalAnswers.persona,
        has_kids: finalAnswers.has_kids,
        financial_abuse: finalAnswers.financial_abuse,
        complexity_level: finalAnswers.complexity_level,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const submitBackInCourt = async (finalBic) => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/quiz/submit`, {
        persona: 'back_in_court',
        has_lawyer: finalBic.has_lawyer,
        court_issue: finalBic.court_issue,
        court_level: finalBic.court_level,
      }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      navigate('/back-in-court');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={styles.loadingPage}>
      <img src="/logo.svg" alt="Elephants Answering" style={styles.loadingLogo} />
      <p style={styles.loadingText}>{isBackInCourt ? 'Finding your stories...' : 'Building your roadmap...'}</p>
      <p style={styles.loadingSubtext}>{isBackInCourt ? 'Based on where you are now.' : 'Tailoring each phase to your situation.'}</p>
    </div>
  );

  return (
    <div style={styles.page}>
      <div style={styles.logoArea} onClick={() => navigate('/')}>
        <img src="/logo.svg" alt="Elephants Answering" style={styles.logoIcon} />
        <div>
          <div style={styles.logoText}>Elephants Answering, PBC</div>
          <div style={styles.logoSub}>Divorce Support & Refuge</div>
        </div>
      </div>

      <div style={styles.card}>
        <p style={styles.intro}>Help us get to know a little about your situation by answering 4 questions that will generate your roadmap.</p>
        <div style={styles.progressTrack}>
          <div style={{ ...styles.progressFill, width: `${pct}%` }} />
        </div>
        <p style={styles.stepLabel}>Question {currentStepNum}</p>
        <h2 style={styles.question}>{current.question}</h2>
        {current.subtext && <p style={styles.subtext}>{current.subtext}</p>}
        <div style={styles.options}>
          {current.type === 'boolean' && current.options.map(opt => (
            <button key={String(opt.value)} style={styles.option} onClick={() => handleAnswer(opt.value)}>
              {opt.label}
            </button>
          ))}
          {current.type === 'select' && current.options.map(opt => {
            const value = typeof opt === 'object' ? opt.value : opt;
            const label = typeof opt === 'object' ? opt.label : opt;
            return (
              <button key={value} style={styles.option} onClick={() => handleAnswer(value)}>
                {label}
              </button>
            );
          })}
        </div>
        {currentStepNum === 1 && (
          <>
            <div style={styles.helpDivider} />
            <button style={styles.helpBtn} onClick={() => navigate('/contact')}>
              I don't know up from down right now — I just need help.
            </button>
          </>
        )}
      </div>
      <Disclaimer />
    </div>
  );
}

const styles = {
  page: { display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh', backgroundColor: colors.cream, padding: '40px 20px' },

  logoArea: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '40px', cursor: 'pointer', alignSelf: 'flex-start', paddingLeft: '20px' },
  logoIcon: { height: '88px', width: 'auto' },
  logoText: { fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', fontWeight: '600', letterSpacing: '0.5px' },
  logoSub: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '11px', letterSpacing: '0.3px', marginTop: '3px' },

  card: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, padding: '48px 40px', maxWidth: '580px', width: '100%' },

  helpDivider: { borderTop: `1px solid ${colors.peach}`, margin: '28px 0 20px' },
  helpBtn: { display: 'block', width: '100%', padding: '14px 20px', backgroundColor: colors.rose, color: colors.white, border: 'none', fontSize: '14px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.3px', lineHeight: '1.5', borderRadius: 0 },
  intro: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.7', marginBottom: '24px', fontStyle: 'italic' },
  progressTrack: { backgroundColor: colors.peach, height: '4px', marginBottom: '28px', borderRadius: 0 },
  progressFill: { backgroundColor: colors.rose, height: '4px', borderRadius: 0, transition: 'width 0.3s ease' },

  stepLabel: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' },
  question: { fontFamily: fonts.heading, color: colors.navy, fontSize: '22px', marginBottom: '10px', lineHeight: '1.5', fontWeight: '600', letterSpacing: '0.3px' },
  subtext: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', marginBottom: '32px', lineHeight: '1.7', fontStyle: 'italic' },

  options: { display: 'flex', flexDirection: 'column', gap: '12px' },
  option: {
    padding: '16px 20px',
    backgroundColor: colors.offWhite,
    border: `1px solid ${colors.peach}`,
    borderRadius: 0,
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    color: colors.navy,
    lineHeight: '1.5',
    fontFamily: fonts.body,
    transition: 'border-color 0.15s, background-color 0.15s',
  },

  loadingPage: { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: colors.cream, gap: '16px' },
  loadingLogo: { height: '56px', marginBottom: '8px', opacity: 0.6 },
  loadingText: { fontFamily: fonts.heading, fontSize: '20px', color: colors.navy, letterSpacing: '0.3px' },
  loadingSubtext: { fontFamily: fonts.body, fontSize: '13px', color: colors.textMedium, fontStyle: 'italic' },
};
